import { useEffect, useState } from "react";
import { toast } from "sonner";
import { admin } from "../../lib/api";
import { panel, inputCls, btnPrimary, btnGhost, PageHead, Field } from "../ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Trash2, Plus } from "lucide-react";

function Announcements() {
  const [list, setList] = useState([]);
  const load = () => admin.get("/admin/announcements").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { await admin.post("/admin/announcements", { messages: [{ text: "NEW ANNOUNCEMENT", link: "" }], variant: "cobalt", rotating: true, interval: 4000, dismissible: true, pages: ["all"], active: true, priority: 1 }); load(); };
  const save = async (a) => { await admin.put(`/admin/announcements/${a.id}`, a); toast.success("Saved"); };
  const del = async (a) => { await admin.delete(`/admin/announcements/${a.id}`); load(); };
  const setMsg = (a, idx, text) => { const m = [...a.messages]; m[idx] = { ...m[idx], text }; setList(list.map((x) => x.id === a.id ? { ...x, messages: m } : x)); };

  return (
    <div className="space-y-4">
      <button onClick={add} className={btnPrimary + " flex items-center gap-1.5"}><Plus size={14} /> Add Bar</button>
      {list.map((a) => (
        <div key={a.id} className={`${panel} p-4 space-y-3`}>
          {a.messages.map((m, i) => (
            <input key={i} className={inputCls} value={m.text} onChange={(e) => setMsg(a, i, e.target.value)} />
          ))}
          <div className="flex items-center gap-3 flex-wrap">
            <select className={`${inputCls} w-32`} value={a.variant} onChange={(e) => setList(list.map((x) => x.id === a.id ? { ...x, variant: e.target.value } : x))}>
              <option value="cobalt">Cobalt</option><option value="black">Black</option><option value="white">White</option>
            </select>
            <label className="text-sm text-zinc-400 flex items-center gap-1.5"><input type="checkbox" checked={a.active} onChange={(e) => setList(list.map((x) => x.id === a.id ? { ...x, active: e.target.checked } : x))} /> Active</label>
            <label className="text-sm text-zinc-400 flex items-center gap-1.5"><input type="checkbox" checked={a.rotating} onChange={(e) => setList(list.map((x) => x.id === a.id ? { ...x, rotating: e.target.checked } : x))} /> Rotating</label>
            <button onClick={() => save(a)} className={btnPrimary}>Save</button>
            <button onClick={() => del(a)} className="text-zinc-500 hover:text-red-400 ml-auto"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Marquees() {
  const [list, setList] = useState([]);
  const load = () => admin.get("/admin/marquees").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { await admin.post("/admin/marquees", { name: "New Marquee", items: [{ text: "SALE LIVE NOW" }], speed: "medium", direction: "left", variant: "white_on_black", separator: "star", height: 48, placements: ["homepage"], active: true }); load(); };
  const save = async (m) => { await admin.put(`/admin/marquees/${m.id}`, m); toast.success("Saved"); };
  const del = async (m) => { await admin.delete(`/admin/marquees/${m.id}`); load(); };
  const upd = (id, k, v) => setList(list.map((x) => x.id === id ? { ...x, [k]: v } : x));

  return (
    <div className="space-y-4">
      <button onClick={add} className={btnPrimary + " flex items-center gap-1.5"}><Plus size={14} /> Add Marquee</button>
      {list.map((m) => (
        <div key={m.id} className={`${panel} p-4 space-y-3`}>
          <input className={inputCls} value={m.name} onChange={(e) => upd(m.id, "name", e.target.value)} />
          <textarea className={inputCls} rows={2} value={m.items.map((i) => i.text).join(" | ")}
            onChange={(e) => upd(m.id, "items", e.target.value.split("|").map((t) => ({ text: t.trim() })))} placeholder="Items separated by |" />
          <div className="flex gap-3 flex-wrap items-center">
            <select className={`${inputCls} w-28`} value={m.speed} onChange={(e) => upd(m.id, "speed", e.target.value)}><option value="slow">Slow</option><option value="medium">Medium</option><option value="fast">Fast</option></select>
            <select className={`${inputCls} w-40`} value={m.variant} onChange={(e) => upd(m.id, "variant", e.target.value)}>
              <option value="white_on_black">White on Black</option><option value="black_on_white">Black on White</option><option value="white_on_cobalt">White on Cobalt</option><option value="outline_on_black">Outline</option>
            </select>
            <select className={`${inputCls} w-40`} value={(m.placements || [])[0] || "homepage"} onChange={(e) => upd(m.id, "placements", [e.target.value])}>
              <option value="homepage">Homepage</option><option value="under_announcement">Under Announcement</option><option value="above_footer">Above Footer</option>
            </select>
            <label className="text-sm text-zinc-400 flex items-center gap-1.5"><input type="checkbox" checked={m.active} onChange={(e) => upd(m.id, "active", e.target.checked)} /> Active</label>
            <button onClick={() => save(m)} className={btnPrimary}>Save</button>
            <button onClick={() => del(m)} className="text-zinc-500 hover:text-red-400 ml-auto"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Navigation() {
  const [items, setItems] = useState([]);
  useEffect(() => { admin.get("/admin/navigation").then((r) => setItems(r.data)); }, []);
  const upd = (i, k, v) => setItems(items.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const save = async () => { await admin.put("/admin/navigation", items); toast.success("Saved"); };
  return (
    <div className={`${panel} p-4 space-y-2`}>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <input className={inputCls} value={it.label} onChange={(e) => upd(i, "label", e.target.value)} />
          <input className={inputCls} value={it.link} onChange={(e) => upd(i, "link", e.target.value)} />
          <label className="flex items-center gap-1 text-xs text-zinc-400 shrink-0"><input type="checkbox" checked={it.visible !== false} onChange={(e) => upd(i, "visible", e.target.checked)} /> Show</label>
          <button onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 size={15} className="text-zinc-500" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => setItems([...items, { label: "New", link: "/", visible: true, order: items.length }])} className={btnGhost}>Add Link</button>
        <button onClick={save} className={btnPrimary}>Save Navigation</button>
      </div>
    </div>
  );
}

function Pages() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => admin.get("/admin/pages").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { await admin.put(`/admin/pages/${sel.id}`, sel); toast.success("Saved"); load(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className={`${panel} p-4 space-y-1 h-fit`}>
        {list.map((p) => <button key={p.id} onClick={() => setSel(p)} className={`block w-full text-left px-3 py-2 text-sm ${sel?.id === p.id ? "text-accent bg-zinc-900" : "text-zinc-400 hover:text-white"}`}>{p.title}</button>)}
      </div>
      {sel && (
        <div className={`${panel} p-4 lg:col-span-2 space-y-3`}>
          <Field label="Title"><input className={inputCls} value={sel.title} onChange={(e) => setSel({ ...sel, title: e.target.value })} /></Field>
          <Field label="Body"><textarea rows={12} className={inputCls} value={sel.body} onChange={(e) => setSel({ ...sel, body: e.target.value })} /></Field>
          <button onClick={save} className={btnPrimary}>Save Page</button>
        </div>
      )}
    </div>
  );
}

export default function Content() {
  return (
    <div>
      <PageHead title="Content" />
      <Tabs defaultValue="announcements">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
          <TabsTrigger value="marquees" data-testid="tab-marquees">Marquees</TabsTrigger>
          <TabsTrigger value="navigation">Navigation</TabsTrigger>
          <TabsTrigger value="pages">Pages</TabsTrigger>
        </TabsList>
        <TabsContent value="announcements" className="mt-5"><Announcements /></TabsContent>
        <TabsContent value="marquees" className="mt-5"><Marquees /></TabsContent>
        <TabsContent value="navigation" className="mt-5"><Navigation /></TabsContent>
        <TabsContent value="pages" className="mt-5"><Pages /></TabsContent>
      </Tabs>
    </div>
  );
}
